import React, { createContext, useContext } from 'react';
import { hasListedPermission } from './recruitmentPermissionAliases';

interface MeetingPermissions {
  canCreateMeetings?: boolean;
  canViewMeetings?: boolean;
  canEditMeetings?: boolean;
  canDeleteMeetings?: boolean;
  canViewAllMeetings?: boolean;
  canReassignMeetings?: boolean;
  canViewReports?: boolean;
  canManageAgentSettings?: boolean;
  canManagePortalSettings?: boolean;
}

interface IPermissionContext {
  permissions: string[];
  currentUserRoles: string[];
  meetingPermissions?: MeetingPermissions | null;
}

const PermissionContext = createContext<IPermissionContext>({ permissions: [], currentUserRoles: [], meetingPermissions: null });

interface PermissionProviderProps {
  permissions: string[];
  currentUserRoles: string[];
  meetingPermissions?: MeetingPermissions | null;
  children: React.ReactNode;
}

export const PermissionProvider = ({ permissions, currentUserRoles, meetingPermissions, children }: PermissionProviderProps) => (
  <PermissionContext.Provider value={{ permissions, currentUserRoles, meetingPermissions }}>
    {children}
  </PermissionContext.Provider>
);

export const usePermissions = () => useContext(PermissionContext);

export function usePermission(module: string, section: string, action: string) {
  const { permissions, currentUserRoles, meetingPermissions } = useContext(PermissionContext);
  if (Array.isArray(currentUserRoles) && currentUserRoles.includes('Superadmin')) {
    return true;
  }
  
  // Check role-based permissions first
  if (hasListedPermission(permissions, module, section, action)) {
    return true;
  }
  
  // For Meetings module, also check MeetingPermission model
  if (module === 'Meetings' && meetingPermissions) {
    if (section === 'Distribution' && action === 'create') {
      return meetingPermissions.canCreateMeetings === true;
    }
    if (section === 'Distribution' && action === 'view') {
      return meetingPermissions.canViewMeetings === true;
    }
    if (section === 'Distribution' && action === 'edit') {
      return meetingPermissions.canEditMeetings === true;
    }
    if (section === 'Calendar' && action === 'view') {
      return meetingPermissions.canViewMeetings === true || meetingPermissions.canViewAllMeetings === true;
    }
    if (section === 'Calendar' && action === 'view_all') {
      return meetingPermissions.canViewAllMeetings === true;
    }
    if (section === 'Calendar' && action === 'reassign') {
      return meetingPermissions.canReassignMeetings === true;
    }
    if (section === 'AgentSettings' && action === 'manage_credentials') {
      return meetingPermissions.canManageAgentSettings === true;
    }
  }
  
  return false;
} 